import { useMemo, useState } from "react";
import { AlertCircle, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";

const DISMISS_KEY_PREFIX = "past_due_banner_dismissed_";

const safeGet = (key: string): string | null => {
  try { return localStorage.getItem(key); } catch { return null; }
};
const safeSet = (key: string, value: string): void => {
  try { localStorage.setItem(key, value); } catch { /* noop */ }
};

/**
 * In-app banner for users whose subscription is in past_due/delayed state.
 * Shown at the top of Inicio so the user knows their payment failed and what
 * to do — without losing access (5-day grace period is in effect).
 *
 * Dismissible for 24h per device.
 */
export default function PastDueBanner() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [dismissedAt, setDismissedAt] = useState(() => safeGet(`${DISMISS_KEY_PREFIX}${user?.id}`));

  const pastDueSub = useMemo(() => {
    if (!user) return null;
    return user.subscriptions.find((s) => s.subscription_status === "past_due") || null;
  }, [user]);

  if (!pastDueSub) return null;

  // Hide if dismissed within last 24h
  if (dismissedAt) {
    const dismissedTime = new Date(dismissedAt).getTime();
    if (Date.now() - dismissedTime < 24 * 60 * 60 * 1000) return null;
  }

  const isEs = i18n.language?.startsWith("es");
  const isDe = i18n.language?.startsWith("de");
  const title = isEs
    ? "No pudimos procesar tu pago"
    : isDe ? "Zahlung konnte nicht verarbeitet werden" : "Payment couldn't be processed";
  const body = isEs
    ? "Tu último cargo de suscripción falló. Estamos reintentando — mantienes acceso por 5 días. Por favor actualiza tu método de pago en Hotmart para no perder el acceso."
    : isDe ? "Ihre letzte Abonnementzahlung ist fehlgeschlagen. Wir versuchen es erneut — Sie haben noch 5 Tage Zugang. Bitte aktualisieren Sie Ihre Zahlungsmethode in Hotmart."
    : "Your last subscription charge failed. We're retrying — you'll keep access for 5 days. Please update your payment method in Hotmart to avoid losing access.";
  const ctaText = isEs ? "Actualizar pago" : isDe ? "Zahlung aktualisieren" : "Update payment";

  const handleDismiss = () => {
    const now = new Date().toISOString();
    safeSet(`${DISMISS_KEY_PREFIX}${user?.id}`, now);
    setDismissedAt(now);
  };

  return (
    <div
      className="mx-4 mt-3 mb-2 rounded-2xl p-3 flex items-start gap-3"
      style={{ border: "0.5px solid #D4B896", backgroundColor: "#F5EDD8" }}
    >
      <AlertCircle size={18} className="mt-0.5 shrink-0" style={{ color: "#B8966A" }} />
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-bold" style={{ color: "#5C3A1E" }}>{title}</p>
        <p className="text-[12px] leading-relaxed mt-0.5" style={{ color: "#7A5C3A" }}>{body}</p>
        <a
          href="https://app-vlc.hotmart.com/subscription"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block mt-2 text-[12px] font-semibold underline underline-offset-2"
          style={{ color: "#B8966A" }}
        >
          {ctaText}
        </a>
      </div>
      <button
        onClick={handleDismiss}
        aria-label={isEs ? "Cerrar" : isDe ? "Schließen" : "Dismiss"}
        className="shrink-0 mt-0.5"
        style={{ color: "#B8966A" }}
      >
        <X size={16} />
      </button>
    </div>
  );
}
